import {
  arrayRemove,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { firebaseApp } from "../../firebase/firebaseApp";

const TEMP_COLLECTION = "temp_clinic_study_ids_unused";

function clean(value) {
  if (value === undefined || value === null) return null;
  const next = String(value).trim();
  return next.length ? next : null;
}

function findAllowedIdMatch(allowedIds, subjectId) {
  if (!Array.isArray(allowedIds) || !subjectId) return null;

  for (const candidate of allowedIds) {
    if (clean(candidate) === subjectId) return candidate;
  }

  return null;
}

async function claimSubjectScopedDoc({ db, mappingRef, subjectId }) {
  const tempRef = doc(db, TEMP_COLLECTION, subjectId);
  const tempSnap = await getDoc(tempRef);

  if (!tempSnap.exists()) return null;

  const clinicStudyId = clean(tempSnap.data()?.clinicStudyId);
  if (!clinicStudyId) {
    return { ok: false, reason: "invalid_temp_mapping" };
  }

  return runTransaction(db, async (tx) => {
    const mappingSnap = await tx.get(mappingRef);
    const existingClinicStudyId = clean(mappingSnap.data()?.clinicStudyId);

    if (existingClinicStudyId) {
      return {
        ok: true,
        subjectId: clean(mappingSnap.data()?.subjectId) ?? subjectId,
        clinicStudyId: existingClinicStudyId,
        source: "existing_mapping",
      };
    }

    const currentTempSnap = await tx.get(tempRef);
    if (!currentTempSnap.exists()) {
      return { ok: false, reason: "not_found" };
    }

    const nextClinicStudyId = clean(currentTempSnap.data()?.clinicStudyId);
    if (!nextClinicStudyId) {
      return { ok: false, reason: "invalid_temp_mapping" };
    }

    tx.set(
      mappingRef,
      {
        subjectId,
        clinicStudyId: nextClinicStudyId,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    tx.delete(tempRef);

    return {
      ok: true,
      subjectId,
      clinicStudyId: nextClinicStudyId,
      source: "subject_scoped_temp_doc",
    };
  });
}

async function claimClinicScopedDoc({ db, mappingRef, subjectId }) {
  const tempCollection = collection(db, TEMP_COLLECTION);
  const exactMatchQuery = query(
    tempCollection,
    where("allowedIds", "array-contains", subjectId),
    limit(1),
  );
  const exactMatchSnapshot = await getDocs(exactMatchQuery);

  let candidateDoc = exactMatchSnapshot.docs[0] ?? null;

  if (!candidateDoc) {
    const tempSnapshot = await getDocs(tempCollection);
    candidateDoc =
      tempSnapshot.docs.find((tempDoc) => {
        const data = tempDoc.data() ?? {};
        return !!findAllowedIdMatch(data.allowedIds, subjectId);
      }) ?? null;
  }

  let candidate = null;

  if (candidateDoc) {
    const data = candidateDoc.data() ?? {};
    const matchedId = findAllowedIdMatch(data.allowedIds, subjectId);
    const clinicStudyId = clean(data.clinicStudyId);

    if (matchedId && clinicStudyId) {
      candidate = {
        ref: candidateDoc.ref,
        clinicStudyId,
        matchedId,
      };
    }
  }

  if (!candidate) return { ok: false, reason: "not_found" };

  return runTransaction(db, async (tx) => {
    const mappingSnap = await tx.get(mappingRef);
    const existingClinicStudyId = clean(mappingSnap.data()?.clinicStudyId);

    if (existingClinicStudyId) {
      return {
        ok: true,
        subjectId: clean(mappingSnap.data()?.subjectId) ?? subjectId,
        clinicStudyId: existingClinicStudyId,
        source: "existing_mapping",
      };
    }

    const candidateSnap = await tx.get(candidate.ref);
    if (!candidateSnap.exists()) {
      return { ok: false, reason: "not_found" };
    }

    const data = candidateSnap.data() ?? {};
    const matchedId = findAllowedIdMatch(data.allowedIds, subjectId);
    const clinicStudyId = clean(data.clinicStudyId) ?? candidate.clinicStudyId;

    if (!matchedId || !clinicStudyId) {
      return { ok: false, reason: "not_found" };
    }

    tx.set(
      mappingRef,
      {
        subjectId,
        clinicStudyId,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    tx.update(candidate.ref, {
      allowedIds: arrayRemove(matchedId),
      updatedAt: serverTimestamp(),
    });

    return {
      ok: true,
      subjectId,
      clinicStudyId,
      source: "clinic_scoped_temp_doc",
    };
  });
}

export async function claimLegacyClinicStudyId({ uid, subjectId }) {
  const nextUid = clean(uid);
  const nextSubjectId = clean(subjectId);

  if (!nextUid || !nextSubjectId) {
    return { ok: false, reason: "missing_subject_id" };
  }

  const db = getFirestore(firebaseApp);
  const mappingRef = doc(db, "user_clinic_mapping", nextUid);

  try {
    const subjectScoped = await claimSubjectScopedDoc({
      db,
      mappingRef,
      subjectId: nextSubjectId,
    });

    if (subjectScoped) return subjectScoped;

    return await claimClinicScopedDoc({
      db,
      mappingRef,
      subjectId: nextSubjectId,
    });
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[clinic-mapping] Failed to claim legacy study id", error);
    }

    return { ok: false, reason: "fetch_failed" };
  }
}
